import { motion } from "framer-motion";
import { ArrowRight, Factory, HeartHandshake, MapPin } from "lucide-react";

const TEAM = [
  {
    name: "Jeswin Joseph",
    role: "Founder",
    bio: "Started Cup Connect to give every tapri and café a brand-sized voice — and every brand a street-sized stage.",
    img: "/images/team/jeswin.jpg",
    accent: "border-[#2E1065]",
  },
  {
    name: "Vishwa Nadig",
    role: "Co Founder",
    bio: "Runs our manufacturing floor — from raw paper roll to packed carton, he owns quality on every batch.",
    img: "/images/team/vishwa.jpg",
    accent: "border-[#FF5A36]",
  },
  {
    name: "Poornima Mohan",
    role: "Marketing Head",
    bio: "Turns printed cups into campus media plans, mapping brands to tapris, cafés and major IT parks across Karnataka.",
    img: "/images/team/poornima.jpg",
    accent: "border-[#0D9488]",
  },
  {
    name: "Evangeline Vianny",
    role: "PR Head",
    bio: "Tells the stories of the vendors and brands behind every cup — press, partnerships and community.",
    img: "/images/team/evangeline.jpg",
    accent: "border-[#FBBF24]",
  },
];

const VALUES = [
  { icon: Factory, title: "Manufactured by us", text: "Every cup comes off our own production lines — no outsourcing, ever." },
  { icon: HeartHandshake, title: "Vendor-first", text: "Chai tapris and cafés earn from the brands printed on the cups they serve." },
  { icon: MapPin, title: "Proudly Indian", text: "Made in our own facility — marketed across Karnataka's major IT parks, tapris and cafés." },
];

export const AboutPage = ({ onQuote }) => (
  <main className="pt-32 pb-0" data-testid="about-page">
    <section className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-3xl"
      >
        <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#FF5A36] mb-4" data-testid="about-eyebrow">
          About Cup Connect
        </p>
        <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-[#2E1065]" data-testid="about-headline">
          The people behind{" "}
          <span className="italic text-[#FF5A36]">the cup.</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-[#2E1065]/70 leading-relaxed" data-testid="about-intro">
          Cup Connect is a cup-based advertising service. Brands pay to put
          their artwork on paper cups, branded tissues and 1-litre glass water
          bottles — all manufactured in our own facility — and our network of
          chai tapris, tea stalls, cafés and IT park food courts serves them to
          thousands of people every day. Vendors cut their packaging costs;
          advertisers get held-not-scrolled brand time; we handle printing,
          placement and reporting.
        </p>
        <p className="mt-4 text-base sm:text-lg text-[#2E1065]/70 leading-relaxed" data-testid="about-stage">
          <span className="font-bold text-[#FF5A36]">Where we are now:</span>{" "}
          we are in our launch stage, onboarding a founding cohort of pilot
          campaigns across Karnataka's major IT parks. Early partners get
          founding-cohort pricing and a direct line to the team below.
        </p>
      </motion.div>

      <div className="mt-14 grid sm:grid-cols-3 gap-5" data-testid="about-values">
        {VALUES.map((v, i) => (
          <motion.div
            key={v.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white rounded-3xl p-7 border border-[#2E1065]/10 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-[#2E1065]/10 transition-all duration-300"
            data-testid={`about-value-${i}`}
          >
            <div className="w-12 h-12 rounded-2xl bg-[#2E1065] flex items-center justify-center mb-5">
              <v.icon className="w-6 h-6 text-[#FBBF24]" />
            </div>
            <p className="font-display font-bold text-lg text-[#2E1065]">{v.title}</p>
            <p className="text-sm text-[#2E1065]/60 mt-2 leading-relaxed">{v.text}</p>
          </motion.div>
        ))}
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#0D9488] mb-4" data-testid="team-eyebrow">
          Meet the Team
        </p>
        <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#2E1065] leading-tight" data-testid="team-headline">
          Small team. <span className="italic text-[#0D9488]">Massive output.</span>
        </h2>
      </motion.div>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="team-grid">
        {TEAM.map((m, i) => (
          <motion.div
            key={m.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="group bg-white rounded-[2rem] p-7 border border-[#2E1065]/10 text-center hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#2E1065]/15 transition-all duration-400"
            data-testid={`team-card-${m.role.toLowerCase().replace(/ & | /g, "-")}`}
          >
            <img
              src={m.img}
              alt={m.name}
              className={`w-28 h-28 rounded-full mx-auto object-cover border-4 ${m.accent} group-hover:scale-105 transition-transform duration-300`}
              data-testid={`team-photo-${m.role.toLowerCase().replace(/ & | /g, "-")}`}
            />
            <p className="mt-5 font-display font-bold text-xl text-[#2E1065]">{m.name}</p>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5A36] mt-1">{m.role}</p>
            <p className="text-sm text-[#2E1065]/60 mt-3 leading-relaxed">{m.bio}</p>
          </motion.div>
        ))}
      </div>
    </section>

    <section className="bg-[#2E1065] py-20 grain relative overflow-hidden" data-testid="about-cta">
      <div className="absolute -top-20 right-10 w-72 h-72 rounded-full bg-[#FF5A36]/20 blur-3xl" />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#FAF7F2] leading-tight" data-testid="about-cta-headline">
          Want your brand rolling off{" "}
          <span className="italic text-[#FBBF24]">our production line?</span>
        </h2>
        <button
          onClick={() => onQuote()}
          className="group mt-8 inline-flex items-center gap-2 font-bold text-[#2E1065] bg-[#FBBF24] hover:bg-[#FF5A36] hover:text-white rounded-full px-8 py-4 transition-all duration-300 hover:-translate-y-1 shadow-xl"
          data-testid="about-cta-quote-button"
        >
          Request Custom Quote
          <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </section>
  </main>
);
